import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { endOfMonth, format, startOfMonth, subDays, subMonths } from "date-fns";
import Admin from "../layout/Admin";
import spinner from "../layout/spinner.gif";
import { getDailyIncome } from "../../actions/dashboard";
import { getPlan } from "../../actions/plan";
import { canAccess } from "../../utils/access";
import TargetBar from "../plan/TargetBar";
import DailyIncomeChart from "./DailyIncomeChart";
import DailyIncomeHeatmap from "./DailyIncomeHeatmap";
import { dayTargetOf, dayTotalsOf, money, targetNote, weekdayOf } from "./incomeLabels";

// The dashboard's day by day page: what each kiosk brought on each day of a range,
// after refunds (GET /api/monitoring/income/daily). The filters on top scope all
// below them: the numbers, the chart of the days, and the kiosks' rows under it.
// Days are Mongolian days, as the browser here reads them. Kept on the page, not in
// the store.

const dayOf = (date) => format(date, "yyyy-MM-dd");

const RANGES = [
  { key: "7days", label: "7 хоног", range: (now) => [subDays(now, 6), now] },
  { key: "30days", label: "30 хоног", range: (now) => [subDays(now, 29), now] },
  { key: "month", label: "Энэ сар", range: (now) => [startOfMonth(now), now] },
  {
    key: "lastMonth",
    label: "Өмнөх сар",
    range: (now) => [startOfMonth(subMonths(now, 1)), endOfMonth(subMonths(now, 1))],
  },
];
const DEFAULT_RANGE = RANGES[1];
// An option's days today, as ["YYYY-MM-DD", "YYYY-MM-DD"]
const daysOf = (option) => option.range(new Date()).map(dayOf);

// The range is in the address (/income?from=YYYY-MM-DD&to=YYYY-MM-DD), so the
// dashboard's link opens its own days and a reload keeps them. An address without
// a range opens the default one.
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const rangeOf = (search) => {
  const params = new URLSearchParams(search);
  const from = params.get("from");
  const to = params.get("to");
  return DAY.test(from) && DAY.test(to) && from <= to ? [from, to] : daysOf(DEFAULT_RANGE);
};

const card = "bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700";
const labelClass = "block text-xs font-medium text-gray-500 dark:text-gray-400";
const fieldClass =
  "block h-10 mt-1 text-sm text-gray-700 bg-white border-gray-300 rounded-lg dark:text-gray-200 dark:bg-gray-700 dark:border-gray-600";

const StatCard = ({ title, value, note, share }) => (
  <div className={"p-5 " + card}>
    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
    <p className="mt-2 text-2xl font-semibold tracking-tight text-gray-800 truncate dark:text-gray-100">
      {value}
    </p>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{note}</p>
    {share !== null && share !== undefined && (
      <div className="mt-3">
        <TargetBar share={share} />
      </div>
    )}
  </div>
);

const DailyIncome = ({ getDailyIncome, getPlan, user }) => {
  const { search, key } = useLocation();
  const history = useHistory();
  const [appliedFrom, appliedTo] = rangeOf(search);

  const [from, setFrom] = useState(appliedFrom);
  const [to, setTo] = useState(appliedTo);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // The kiosk shown alone, or "" for all of them
  const [kioskId, setKioskId] = useState("");
  const [dayTarget, setDayTarget] = useState(0);

  // The target comes from the Plan page, which needs its own access
  const canSeePlan = canAccess(user, "plan");
  useEffect(() => {
    if (!canSeePlan) return;
    let left = false;
    getPlan().then((plan) => !left && setDayTarget(dayTargetOf(plan)));
    return () => {
      left = true;
    };
  }, [canSeePlan, getPlan]);

  // key is new on every pick, so picking the range on screen again loads it again
  useEffect(() => {
    // Only the newest request may fill the page
    let stale = false;
    setFrom(appliedFrom);
    setTo(appliedTo);
    setLoading(true);
    getDailyIncome({ startDate: appliedFrom, endDate: appliedTo }).then((daily) => {
      if (stale) return;
      setLoading(false);
      setError(daily.error || null);
      if (daily.report) setReport(daily.report);
    });
    return () => {
      stale = true;
    };
  }, [appliedFrom, appliedTo, key, getDailyIncome]);

  const apply = (start, end) => history.replace(`/income?from=${start}&to=${end}`);

  const submitCustom = (e) => {
    e.preventDefault();
    if (!from || !to || from > to) return;
    apply(from, to);
  };

  const active = RANGES.find((option) => {
    const [start, end] = daysOf(option);
    return start === appliedFrom && end === appliedTo;
  });

  const days = report ? report.days : [];
  const kiosks = report ? report.kiosks : [];
  const totals = useMemo(() => (report ? dayTotalsOf(report) : []), [report]);
  // A kiosk without income in the range on screen is not in it; all are shown then
  const kiosk = kiosks.find((item) => String(item.id) === kioskId) || null;

  const sumOf = (values) => values.reduce((sum, value) => sum + value, 0);
  const values = kiosk ? kiosk.days : totals;
  const income = sumOf(values);
  const best = values.reduce((top, value, index) => (value > values[top] ? index : top), 0);
  // The target is that of all the kiosks together, so one kiosk has none
  const target = kiosk ? 0 : dayTarget;
  const withIncome = values.filter((value) => value > 0).length;

  const lastStat = kiosk
    ? {
        title: "Орлоготой өдөр",
        value: `${withIncome} / ${days.length}`,
        note:
          withIncome === days.length
            ? "Өдөр бүр орлоготой"
            : `${days.length - withIncome} өдөр орлогогүй`,
      }
    : target > 0
    ? {
        title: "Зорилтод хүрсэн өдөр",
        value: `${values.filter((value) => value >= target).length} / ${days.length}`,
        note: `Өдрийн зорилт ${money(target)}`,
      }
    : {
        title: "Орлоготой киоск",
        value: String(kiosks.length),
        note: "Сонгосон хугацаанд",
      };

  return (
    <Admin>
      {/* min-w-0: the layout's grid would otherwise widen the page to the table's
          width on a phone, instead of letting it scroll */}
      <div className="min-w-0 pb-10">
        <div className="mt-6 mb-6">
          <Link
            to="/dashboard"
            className="text-sm font-medium text-purple-600 hover:underline dark:text-purple-400"
          >
            ← Хяналтын самбар
          </Link>
          <h1 className="mt-1 text-2xl font-semibold text-gray-700 dark:text-gray-200">
            Өдөр тутмын орлого
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Киоск бүрийн өдрийн орлого, буцаалтыг хассан дүнгээр
          </p>
        </div>

        {/* One row of filters; everything below follows it */}
        <section className={"p-4 mb-6 " + card}>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-4">
            <div>
              <span className={labelClass + " mb-1"}>Хугацаа</span>
              <div className="flex flex-wrap gap-2">
                {RANGES.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => apply(...daysOf(option))}
                    aria-pressed={active === option}
                    className={`h-10 px-4 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                      active === option
                        ? "text-white bg-purple-600 shadow-sm"
                        : "text-gray-600 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <label className={labelClass}>
              Киоск
              <select
                value={kiosk ? kioskId : ""}
                onChange={(e) => setKioskId(e.target.value)}
                className={fieldClass + " w-56"}
              >
                <option value="">Бүх киоск</option>
                {kiosks.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name || "Нэргүй киоск"}
                  </option>
                ))}
              </select>
            </label>
            <form onSubmit={submitCustom} className="flex flex-wrap items-end gap-3 xl:ml-auto">
              <label className={labelClass}>
                Эхлэх огноо
                <input
                  type="date"
                  value={from}
                  max={to}
                  onChange={(e) => setFrom(e.target.value)}
                  className={fieldClass + " w-full"}
                />
              </label>
              <label className={labelClass}>
                Дуусах огноо
                <input
                  type="date"
                  value={to}
                  min={from}
                  max={dayOf(new Date())}
                  onChange={(e) => setTo(e.target.value)}
                  className={fieldClass + " w-full"}
                />
              </label>
              <button
                type="submit"
                disabled={loading || !from || !to || from > to}
                className="h-10 px-5 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:text-gray-800 dark:bg-gray-100"
              >
                Харах
              </button>
            </form>
          </div>
        </section>

        {error && (
          <div className="px-4 py-3 mb-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50 dark:text-red-200 dark:bg-red-900 dark:border-red-700">
            {error}
            {report && " Өмнөх мэдээллийг харуулж байна."}
          </div>
        )}

        {!report ? (
          loading && (
            <img src={spinner} className="my-6" style={{ width: "50px", margin: "auto", display: "block" }} alt="Loading..." />
          )
        ) : (
          // The last report stays, dimmed, while the next one loads
          <div className={"transition-opacity " + (loading ? "opacity-50" : "")}>
            <p className="mb-3 text-sm text-gray-500 dark:text-gray-400">
              {days[0]} — {days[days.length - 1]} · {days.length} өдөр
              {kiosk && ` · ${kiosk.name || "Нэргүй киоск"}`}
            </p>
            {!kiosk && report.bundles && report.bundles.income > 0 && (
              <p className="mb-3 text-sm text-purple-700 dark:text-purple-300">
                Нийт орлогод вэбээр зарсан {report.bundles.count} багц, {money(report.bundles.income)} орсон.
                Багцаар хэвлэсэн хэвлэлт киоскийн орлогод орохгүй.
              </p>
            )}

            <div className="grid gap-4 mb-6 sm:grid-cols-2 xl:grid-cols-4">
              <StatCard
                title="Нийт орлого"
                value={money(income)}
                note={
                  kiosk
                    ? `Нийт орлогын ${
                        sumOf(totals) > 0 ? Math.round((income / sumOf(totals)) * 100) : 0
                      }%`
                    : target > 0
                    ? targetNote(income, target * days.length, days.length)
                    : `${kiosks.length} киоскоос`
                }
                share={target > 0 ? income / (target * days.length) : null}
              />
              <StatCard
                title="Өдрийн дундаж"
                value={money(income / days.length, { maximumFractionDigits: 0 })}
                note={`${days.length} өдрөөр`}
              />
              <StatCard
                title="Хамгийн өндөр өдөр"
                value={money(values[best])}
                note={values[best] > 0 ? `${days[best]} · ${weekdayOf(days[best])}` : "Орлого алга"}
              />
              <StatCard {...lastStat} />
            </div>

            {/* Without income the kiosks' card below says so, once */}
            {kiosks.length > 0 && (
            <section className={"p-5 mb-6 " + card}>
              <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                <div>
                  <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">
                    {kiosk ? `${kiosk.name || "Нэргүй киоск"} · өдрөөр` : "Нийт орлого, өдрөөр"}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {kiosk ? kiosk.location || "Байршилгүй" : "Бүх киоскийн нийлбэр"}
                  </p>
                </div>
                {kiosk && (
                  <button
                    type="button"
                    onClick={() => setKioskId("")}
                    className="text-sm font-semibold text-purple-600 hover:underline focus:outline-none dark:text-purple-400"
                  >
                    Бүх киоскийг харах
                  </button>
                )}
              </div>
              <DailyIncomeChart days={days} values={values} target={target} />
            </section>
            )}

            <DailyIncomeHeatmap
              days={days}
              kiosks={kiosks}
              totals={totals}
              selected={kiosk ? kioskId : ""}
              onSelect={setKioskId}
            />
          </div>
        )}
      </div>
    </Admin>
  );
};

StatCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  note: PropTypes.string.isRequired,
  share: PropTypes.number,
};

DailyIncome.propTypes = {
  getDailyIncome: PropTypes.func.isRequired,
  getPlan: PropTypes.func.isRequired,
  user: PropTypes.object,
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps, { getDailyIncome, getPlan })(DailyIncome);
